<template>
  <div>
    <v-data-table
      :headers="approvalProcessTypesHeaders"
      :items="filteredItems"
      style="border: solid 1px;"
      :show-select="showSelect"
      :sort-by="[{ key: 'name', order: 'asc' }]"
    >
      <template v-slot:top>
        <v-row dense class="px-3">
          <v-col cols="12">
            <v-text-field
              label="Search by name"
              variant="underlined"
              v-model="nameSearch"
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
              maxlength="200"
            >
            </v-text-field>
          </v-col>
          <v-col cols="6">            
            <v-autocomplete
              :custom-filter="customFilter"
              :items="owners"
              item-title="displayName"
              variant="underlined"
              item-value="id"
              return-object
              label="Owner"
              hide-no-data
              v-model="searchOwner"
              hide-selected
              clearable
            ></v-autocomplete>
          </v-col>
          <v-col cols="6">
            <v-autocomplete
              :items="changeCategories"
              item-title="name"
              item-value="clientID"
              variant="underlined"
              return-object
              label="Change Category"
              hide-no-data
              v-model="changeCategory"
              hide-selected
              clearable
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row justify="end" dense>
          <v-col cols="12" sm="3" class="py-0 mr-3" style="display: flex; justify-content: flex-end">
            <div v-if="showSelect">
              <v-btn small color="green" class="white--text mr-2" @click="next()"><v-icon>mdi-check</v-icon>Continue</v-btn>
              <v-btn small color="red" class="white--text" @click="cancel"><v-icon>mdi-close</v-icon>Cancel</v-btn>
            </div>
            <v-menu top close-on-click v-if="!showSelect">
              <template v-slot:activator="{ props }">
                <v-btn color="primary" class="white-text" small v-bind="props">
                  Bulk Actions <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-item @click="addFAT">
                  <v-list-item-title>Add Approvers, FYIs</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </template>
      <template v-slot:header.data-table-select="{ on, props }">
        <v-checkbox
          color="green"
          :model-value="selected && selected.length === 0 ? false : selected && filteredItems.length === selected.length"
          @update:modelValue="onMultiSelectChange($event)"
          :indeterminate="selected && selected.length > 0 && filteredItems.length !== selected.length"
        ></v-checkbox>
      </template>
      <template v-slot:item="props">
        <tr>
          <td v-if="showSelect">
            <v-checkbox
              color="green"
              :model-value="props.item.isSelected"
              hide-details
              @update:modelValue="onItemCheckboxChange($event, props.item)"
            ></v-checkbox>
          </td>
          <td>
            <a @click="editApprovalProcessType(props.item)" class="blue-link">{{ props.item.name }}</a>
          </td>
          <td>{{ props.item.changeCategory.name || '' }} {{ props.item.changeCategory.enabled ? '' : '(Disabled)' }}</td>
          <td style="min-width: 260px">
            <div class="d-flex" v-if="ownerToEdit.clientID !== props.item.clientID">
              <span class="flex-1">{{ props.item.owner ? props.item.owner.displayName : '' }}</span>
              <v-icon color="orange" @click="editOwner(props.item)">mdi-pencil-outline</v-icon>
            </div>
            <div class="d-flex" v-if="ownerToEdit.clientID === props.item.clientID">
              <div class="flex-1">
                <people-picker
                  v-model="ownerToEdit.owner"
                  :user="props.item.owner"
                  hideDetails
                  @clear="ownerToEdit.owner = null"
                ></people-picker>
              </div>
              <span class="owner-separator">
                <v-icon class="ml-3" color="success" @click="saveOwner(ownerToEdit)">mdi-check</v-icon>
                |
                <v-icon color="red" @click="cancelOwnerEdition">mdi-close</v-icon>
              </span>
            </div>
          </td>
          <td>{{ props.item.approvalDays }}</td>
          <td>
            {{ approvalTypesMap[props.item.approvalType] ? approvalTypesMap[props.item.approvalType].value : '' }}
          </td>
          <td>{{ props.item.approvers ? props.item.approvers.length : 0 }}</td>
          <td>{{ props.item.fyis ? props.item.fyis.length : 0 }}</td>
          <td>{{ props.item.displayOrder }}</td>
          <td><v-icon color="red" @click="deleteApprovalProcessType(props.item)">mdi-delete-outline</v-icon></td>
        </tr>
      </template>
    </v-data-table>
    <v-dialog persistent v-model="showConfirmationDialog" max-width="500">
      <v-card v-if="showConfirmationDialog">
        <v-container>
          <h3>Are you sure you want to delete '{{ selectedApprovalProcessType.name }}' ?</h3>
          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="red" text @click="onDeleteConfirmation">Yes</v-btn>
            <v-btn color="black" text @click="cancelDeletion">No</v-btn>
          </v-card-actions>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ApprovalTypes } from '@/common/types'
import PeoplePicker from '@/components/peoplePicker/PeoplePicker'

export default {
  name: 'ApprovalProcessTypeTable',
  props: {
    approvalProcessTypes: Array,
    changeCategories: Array
  },
  components: {
    PeoplePicker
  },
  data() {
    return {
      approvalTypesMap: ApprovalTypes.properties,
      approvalProcessTypesHeaders: [
        {
          title: 'Name',
          key: 'name'
        },
        {
          title: 'Change Category',
          key: 'changeCategory.name'
        },
        {
          title: 'Owner',
          key: 'owner.displayName'
        },
        {
          title: 'Approval Days',
          key: 'approvalDays'
        },
        {
          title: 'Approval Type',
          key: 'approvalType'
        },
        {
          title: 'Approvers',
          key: 'approvers.length'
        },
        {
          title: 'FYIs',
          key: 'fyis.length'
        },
        {
          title: 'Display Order',
          key: 'displayOrder'
        },
        {
          title: '',
          sortable: false
        }
      ],
      searchOwner: undefined,
      changeCategory: undefined,
      ownerToEdit: {},
      showConfirmationDialog: false,
      selectedApprovalProcessType: undefined,
      showSelect: false,
      selected: [],
      next: () => {},
      nameSearch: ''
    }
  },
  computed: {
    filteredItems() {
      let result = this.approvalProcessTypes
      if (this.searchOwner) {
        result = result.filter(r => r.owner && this.searchOwner.id === r.owner.id)
      }

      if (this.changeCategory) {
        result = result.filter(r => this.changeCategory.clientID === r.changeCategory.clientID)
      }

      if (this.nameSearch && this.nameSearch.trim() && this.nameSearch.trim().length >= 3) {
        result = result.filter(t =>
          t.name
            .trim()
            .toLowerCase()
            .includes(this.nameSearch.trim().toLowerCase())
        )
      }

      return result.map(r => ({ ...r, isSelected: this.selected.findIndex(s => s.clientID === r.clientID) !== -1 }))
    },
    owners() {
      // return this.approvalProcessTypes.filter(r => r.owner).map(r => r.owner)
      const arr = this.approvalProcessTypes.filter(r => r.owner).map(r => r.owner)
      return Array.from(new Map(arr.map(obj => [obj.id, obj])).values())
    }
  },
  methods: {
    customFilter (itemTitle, queryText, item) {
      const textOne = item.raw.displayName.toLowerCase()
      // const textTwo = item.raw.abbr.toLowerCase()
      const searchText = queryText.toLowerCase()
      return textOne.indexOf(searchText) > -1
      // return textOne.indexOf(searchText) > -1 ||
      //   textTwo.indexOf(searchText) > -1
    },    
    onMultiSelectChange(value) {
      if (value) {
        this.selected = this.filteredItems
      } else {
        this.selected = []
      }
    },
    addFAT() {
      this.showSelect = true
      this.next = this.openFATDialog
    },
    openFATDialog() {
      this.showSelect = false
      this.$emit('openFAT', this.selected, 'approvalProcessTypes')
      this.selected = []
    },
    onItemCheckboxChange(value, item) {
      if (value) {
        this.selected.push(item)
      } else {
        this.selected = this.selected.filter(s => s.clientID !== item.clientID)
      }
    },
    cancel() {
      this.selected = []
      this.showSelect = false
    },
    editApprovalProcessType(item) {
      this.$emit('edit', item)
    },
    editOwner(item) {
      this.ownerToEdit = JSON.parse(JSON.stringify(item))
    },
    cancelOwnerEdition() {
      this.ownerToEdit = {}
    },
    saveOwner(item) {
      this.$emit('save', item)
      this.ownerToEdit = {}
    },
    deleteApprovalProcessType(item) {
      this.selectedApprovalProcessType = item
      this.showConfirmationDialog = true
    },
    onDeleteConfirmation() {
      this.showConfirmationDialog = false
      this.$emit('delete', this.selectedApprovalProcessType)
      this.selectedApprovalProcessType = undefined
    },
    cancelDeletion() {
      this.showConfirmationDialog = false
      this.selectedApprovalProcessType = undefined
    }
  }
}
</script>
<style scoped>
.owner-separator {
  position: relative;
  top: 15px;
  font-size: 20px;
  font-weight: 100;
  margin-left: 8px;
  margin-right: 8px;
}

.flex-1 {
  flex: 1;
}
</style>
